import React, { useEffect, useCallback, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Drawer,
} from "@mui/material";
import {
  SearchOutlined,
  KeyboardArrowDown as ArrowDownIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  WarningAmberRounded as WarningIcon,
  CalendarTodayOutlined as CalendarIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { fetchMyTasks, changeWorkOrderStatus } from "../../store/workOrdersSlice";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import useChangeHighlight from "../../../../shared/hooks/useChangeHighlight";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import {
  WorkOrder,
  WorkOrderStatusCode,
  WorkOrderPriorityCode,
  WorkOrderTypeCode,
} from "../../models/models";
import { RootState } from "../../../../core/store";
import TaskListItem from "./TaskListItem";
import CompletionDialog from "../workBoard/CompletionDialog";
import Loader from "../../../../shared/components/Loader";
import TableEmpty from "../../../../shared/components/tables/TableEmpty";
import dayjs from "dayjs";

// ─── Sort options ────────────────────────────────────────────
type SortMode = "newest" | "oldest" | "critical" | "dueDate" | "titleAZ";

interface SortOption {
  key: SortMode;
  labelKey: string;
}

const SORT_OPTIONS: SortOption[] = [
  { key: "newest", labelKey: "myTasks.sort.newestFirst" },
  { key: "oldest", labelKey: "myTasks.sort.oldestFirst" },
  { key: "critical", labelKey: "myTasks.sort.highestPriority" },
  { key: "dueDate", labelKey: "myTasks.sort.dueDate" },
  { key: "titleAZ", labelKey: "myTasks.sort.titleAZ" },
];

// ─── Status filter config ────────────────────────────────────
type StatusFilter = "all" | "notStarted" | "inProgress" | "onHold" | "resolved";

interface StatusChipConfig {
  key: StatusFilter;
  labelKey: string;
}

const STATUS_CHIPS: StatusChipConfig[] = [
  { key: "all", labelKey: "myTasks.filterAll" },
  { key: "notStarted", labelKey: "myTasks.filterNotStarted" },
  { key: "inProgress", labelKey: "myTasks.filterInProgress" },
  { key: "onHold", labelKey: "myTasks.filterOnHold" },
  { key: "resolved", labelKey: "myTasks.filterResolved" },
];

// ─── Priority filter config ──────────────────────────────────
interface PriorityChipConfig {
  code: number;
  labelKey: string;
  color: string;
}

const PRIORITY_CHIPS: PriorityChipConfig[] = [
  { code: WorkOrderPriorityCode.Critical, labelKey: "priorities.critical", color: "#DC2626" },
  { code: WorkOrderPriorityCode.High, labelKey: "priorities.high", color: "#E8A817" },
  { code: WorkOrderPriorityCode.Normal, labelKey: "priorities.normal", color: "#3B82F6" },
  { code: WorkOrderPriorityCode.Low, labelKey: "priorities.low", color: "#8C9099" },
];

// ─── Type filter config ──────────────────────────────────────
interface TypeChipConfig {
  code: number;
  labelKey: string;
}

const TYPE_CHIPS: TypeChipConfig[] = [
  { code: WorkOrderTypeCode.Reactive, labelKey: "typesShort.reactive" },
  { code: WorkOrderTypeCode.PreventiveMaintenance, labelKey: "typesShort.preventiveMaintenance" },
  { code: WorkOrderTypeCode.Inspection, labelKey: "typesShort.inspection" },
  { code: WorkOrderTypeCode.GeneralTask, labelKey: "typesShort.generalTask" },
];

// ─── Time-horizon grouping ──────────────────────────────────
type TimeHorizon = "overdue" | "today" | "thisWeek" | "later" | "noDueDate";

interface TimeHorizonGroup {
  key: TimeHorizon;
  label: string;
  color: string;
  dotColor: string;
  items: WorkOrder[];
}

function getTimeHorizon(wo: WorkOrder): TimeHorizon {
  if (!wo.dueDate) return "noDueDate";
  const now = dayjs().startOf("day");
  const due = dayjs(wo.dueDate).startOf("day");
  const diff = due.diff(now, "day");
  if (diff < 0 && wo.workOrderStatusId < 6) return "overdue";
  if (diff === 0) return "today";
  if (diff <= 6) return "thisWeek"; // rest of current week
  return "later";
}

// ─── Helpers ─────────────────────────────────────────────────

/** Extract distinct machines from tasks */
function extractMachines(tasks: WorkOrder[]) {
  const map = new Map<string, { id: string; name: string; sn: string; count: number }>();
  for (const t of tasks) {
    if (!t.machineId || !t.machineName) continue;
    const existing = map.get(t.machineId);
    if (existing) existing.count++;
    else map.set(t.machineId, { id: t.machineId, name: t.machineName, sn: t.machineSerialNumber || "", count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/** Extract distinct locations from tasks (hierarchical) */
function extractLocations(tasks: WorkOrder[]) {
  const parentMap = new Map<string, { name: string; children: Map<string, { name: string; count: number }> }>();
  for (const t of tasks) {
    const parentName = t.parentLocationName || t.locationName || "";
    const childName = t.locationName || "";
    if (!parentName && !childName) continue;
    const effectiveParent = parentName || childName;
    if (!parentMap.has(effectiveParent)) {
      parentMap.set(effectiveParent, { name: effectiveParent, children: new Map() });
    }
    const parent = parentMap.get(effectiveParent)!;
    if (childName && childName !== effectiveParent) {
      const existing = parent.children.get(childName);
      if (existing) existing.count++;
      else parent.children.set(childName, { name: childName, count: 1 });
    } else {
      const existing = parent.children.get(effectiveParent);
      if (existing) existing.count++;
      else parent.children.set(effectiveParent, { name: effectiveParent, count: 1 });
    }
  }
  return parentMap;
}

const isOverdue = (dueDate?: string, statusId?: number): boolean => {
  if (!dueDate) return false;
  if (statusId && statusId >= 6) return false;
  return dayjs(dueDate).startOf("day").isBefore(dayjs().startOf("day"));
};

// ─── Centered content max-width ─────────────────────────────
const CONTENT_MAX_WIDTH = "1100px";

// ─── Component ───────────────────────────────────────────────

const MyTasksPage = () => {
  useRedesignSetup();
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canWrite } = usePermission(Modules.Maintenance);
  const { isPhone, isTablet, isDesktop } = useBreakpoint();

  const { myTasks, myTasksLoading } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  // ─── Filter state ──────────────────────────────────────────
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedPriorities, setSelectedPriorities] = useState<Set<number>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<number>>(new Set());
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<string>>(new Set());
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ─── Phone bottom sheet state ─────────────────────────────
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  const [tempStatusFilter, setTempStatusFilter] = useState<StatusFilter>("all");
  const [tempPriorities, setTempPriorities] = useState<Set<number>>(new Set());
  const [tempTypes, setTempTypes] = useState<Set<number>>(new Set());
  const [tempMachineIds, setTempMachineIds] = useState<Set<string>>(new Set());
  const [tempLocations, setTempLocations] = useState<Set<string>>(new Set());
  const [tempSortMode, setTempSortMode] = useState<SortMode>("newest");
  const [tempMachineSearch, setTempMachineSearch] = useState("");

  // ─── Date range filter ─────────────────────────────────────
  type DateRangeKey = "7d" | "30d" | "90d" | "all";

  interface DateRangeOptionConfig {
    key: DateRangeKey;
    labelKey: string;
    days: number | null;
  }

  const DATE_RANGES: DateRangeOptionConfig[] = [
    { key: "7d", labelKey: "myTasks.dateRange.last7days", days: 7 },
    { key: "30d", labelKey: "myTasks.dateRange.last30days", days: 30 },
    { key: "90d", labelKey: "myTasks.dateRange.last90days", days: 90 },
    { key: "all", labelKey: "myTasks.dateRange.allTime", days: null },
  ];
  const [dateRange, setDateRange] = useState<DateRangeKey>("30d");
  const [dateRangeMenuAnchor, setDateRangeMenuAnchor] = useState<null | HTMLElement>(null);

  const dateRangeCutoff = useMemo(() => {
    const opt = DATE_RANGES.find((r) => r.key === dateRange);
    if (!opt || opt.days === null) return null;
    return dayjs().subtract(opt.days, "day").startOf("day");
  }, [dateRange]);

  // ─── Dropdown anchors ──────────────────────────────────────
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [machineMenuAnchor, setMachineMenuAnchor] = useState<null | HTMLElement>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] = useState<null | HTMLElement>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [machineSearch, setMachineSearch] = useState("");

  // ─── Completion dialog state ──────────────────────────────
  const [completionTarget, setCompletionTarget] = useState<WorkOrder | null>(null);

  // ─── Data fetching ─────────────────────────────────────────
  const loadData = useCallback(() => {
    dispatch(fetchMyTasks());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDataRefresh("myTasks", loadData);
  useVisibilityRefresh(loadData);

  const changedIds = useChangeHighlight(
    myTasks,
    t => t.id,
    t => `${t.workOrderStatusId}-${t.modifiedDate}`
  );

  // ─── Extract filter options ────────────────────────────────
  const machines = useMemo(() => extractMachines(myTasks), [myTasks]);
  const locations = useMemo(() => extractLocations(myTasks), [myTasks]);

  // ─── Active filter tracking ────────────────────────────────
  const hasActiveFilters = statusFilter !== "all" || selectedPriorities.size > 0 ||
    selectedTypes.size > 0 || selectedMachineIds.size > 0 ||
    selectedLocations.size > 0 || searchTerm.trim().length > 0;

  // ─── Filtering + Sorting ───────────────────────────────────
  const filteredAndSorted = useMemo(() => {
    let items = [...myTasks];

    // Date range filter
    if (dateRangeCutoff) {
      items = items.filter(t => {
        const d = t.createdDate;
        if (!d) return true;
        return dayjs(d).isAfter(dateRangeCutoff);
      });
    }

    // Status filter
    if (statusFilter === "notStarted") {
      items = items.filter(t =>
        t.workOrderStatusId === WorkOrderStatusCode.Assigned ||
        t.workOrderStatusId === WorkOrderStatusCode.Scheduled
      );
    } else if (statusFilter === "inProgress") {
      items = items.filter(t => t.workOrderStatusId === WorkOrderStatusCode.InProgress);
    } else if (statusFilter === "onHold") {
      items = items.filter(t => t.workOrderStatusId === WorkOrderStatusCode.OnHold);
    } else if (statusFilter === "resolved") {
      items = items.filter(t =>
        t.workOrderStatusId === WorkOrderStatusCode.Completed ||
        t.workOrderStatusId === WorkOrderStatusCode.Verified ||
        t.workOrderStatusId === WorkOrderStatusCode.PendingReview
      );
    }

    // Priority filter (multi-select)
    if (selectedPriorities.size > 0) {
      items = items.filter(t => selectedPriorities.has(t.priorityCode));
    }

    // Type filter (multi-select)
    if (selectedTypes.size > 0) {
      items = items.filter(t => selectedTypes.has(t.workOrderTypeId));
    }

    // Machine filter
    if (selectedMachineIds.size > 0) {
      items = items.filter(t => t.machineId && selectedMachineIds.has(t.machineId));
    }

    // Location filter
    if (selectedLocations.size > 0) {
      items = items.filter(t => {
        const locName = t.locationName || "";
        const parentLocName = t.parentLocationName || "";
        return selectedLocations.has(locName) || selectedLocations.has(parentLocName);
      });
    }

    // Search filter (client-side)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter(t =>
        (t.title?.toLowerCase().includes(term)) ||
        (t.number?.toLowerCase().includes(term)) ||
        (t.machineName?.toLowerCase().includes(term)) ||
        (t.locationName?.toLowerCase().includes(term))
      );
    }

    // Sort — overdue items always float to top regardless of sort mode
    items.sort((a, b) => {
      const aOverdue = isOverdue(a.dueDate, a.workOrderStatusId) ? 0 : 1;
      const bOverdue = isOverdue(b.dueDate, b.workOrderStatusId) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;

      switch (sortMode) {
        case "newest":
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        case "oldest":
          return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
        case "critical": {
          if (a.priorityCode !== b.priorityCode) return a.priorityCode - b.priorityCode;
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        }
        case "dueDate": {
          if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          if (a.dueDate) return -1;
          if (b.dueDate) return 1;
          return 0;
        }
        case "titleAZ":
          return (a.title || "").localeCompare(b.title || "");
        default:
          return 0;
      }
    });

    return items;
  }, [myTasks, statusFilter, selectedPriorities, selectedTypes, selectedMachineIds, selectedLocations, searchTerm, sortMode, dateRangeCutoff]);

  // ─── Time-horizon groups (only when sortMode === "dueDate") ─
  const timeHorizonGroups = useMemo((): TimeHorizonGroup[] | null => {
    if (sortMode !== "dueDate") return null;

    const groups: Record<TimeHorizon, WorkOrder[]> = {
      overdue: [],
      today: [],
      thisWeek: [],
      later: [],
      noDueDate: [],
    };

    for (const wo of filteredAndSorted) {
      groups[getTimeHorizon(wo)].push(wo);
    }

    const configs: { key: TimeHorizon; labelKey: string; color: string; dotColor: string }[] = [
      { key: "overdue", labelKey: "myTasks.sectionOverdue", color: "#DC2626", dotColor: "#DC2626" },
      { key: "today", labelKey: "myTasks.sectionToday", color: "#E8A817", dotColor: "#E8A817" },
      { key: "thisWeek", labelKey: "myTasks.sectionThisWeek", color: "#6B7280", dotColor: "#6B7280" },
      { key: "later", labelKey: "myTasks.sectionLater", color: "#9CA3AF", dotColor: "#9CA3AF" },
      { key: "noDueDate", labelKey: "myTasks.sectionNoDueDate", color: "#9CA3AF", dotColor: "#D1D5DB" },
    ];

    return configs
      .filter(c => groups[c.key].length > 0)
      .map(c => ({
        key: c.key,
        label: translate(c.labelKey),
        color: c.color,
        dotColor: c.dotColor,
        items: groups[c.key],
      }));
  }, [filteredAndSorted, sortMode, translate]);

  // ─── Filter handlers ───────────────────────────────────────
  const togglePriority = useCallback((code: number) => {
    setSelectedPriorities(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const toggleType = useCallback((code: number) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const toggleMachine = useCallback((machineId: string) => {
    setSelectedMachineIds(prev => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  }, []);

  const toggleLocation = useCallback((locName: string) => {
    setSelectedLocations(prev => {
      const next = new Set(prev);
      if (next.has(locName)) next.delete(locName);
      else next.add(locName);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setStatusFilter("all");
    setSelectedPriorities(new Set());
    setSelectedTypes(new Set());
    setSelectedMachineIds(new Set());
    setSelectedLocations(new Set());
    setSearchTerm("");
    setSearchExpanded(false);
  }, []);

  const removeFilterChip = useCallback((type: string, value: string | number) => {
    switch (type) {
      case "status": setStatusFilter("all"); break;
      case "priority": setSelectedPriorities(prev => { const n = new Set(prev); n.delete(value as number); return n; }); break;
      case "type": setSelectedTypes(prev => { const n = new Set(prev); n.delete(value as number); return n; }); break;
      case "machine": setSelectedMachineIds(prev => { const n = new Set(prev); n.delete(value as string); return n; }); break;
      case "location": setSelectedLocations(prev => { const n = new Set(prev); n.delete(value as string); return n; }); break;
      case "search": setSearchTerm(""); setSearchExpanded(false); break;
    }
  }, []);

  // ─── Phone bottom sheet handlers ──────────────────────────
  const openPhoneFilters = useCallback(() => {
    setTempStatusFilter(statusFilter);
    setTempPriorities(new Set(selectedPriorities));
    setTempTypes(new Set(selectedTypes));
    setTempMachineIds(new Set(selectedMachineIds));
    setTempLocations(new Set(selectedLocations));
    setTempSortMode(sortMode);
    setTempMachineSearch("");
    setPhoneFiltersOpen(true);
  }, [statusFilter, selectedPriorities, selectedTypes, selectedMachineIds, selectedLocations, sortMode]);

  const applyPhoneFilters = useCallback(() => {
    setStatusFilter(tempStatusFilter);
    setSelectedPriorities(new Set(tempPriorities));
    setSelectedTypes(new Set(tempTypes));
    setSelectedMachineIds(new Set(tempMachineIds));
    setSelectedLocations(new Set(tempLocations));
    setSortMode(tempSortMode);
    setPhoneFiltersOpen(false);
  }, [tempStatusFilter, tempPriorities, tempTypes, tempMachineIds, tempLocations, tempSortMode]);

  const resetPhoneFilters = useCallback(() => {
    setTempStatusFilter("all");
    setTempPriorities(new Set());
    setTempTypes(new Set());
    setTempMachineIds(new Set());
    setTempLocations(new Set());
    setTempSortMode("newest");
    setTempMachineSearch("");
  }, []);

  const toggleTempPriority = useCallback((code: number) => {
    setTempPriorities(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const toggleTempType = useCallback((code: number) => {
    setTempTypes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  // Active filter count for phone badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    count += selectedPriorities.size;
    count += selectedTypes.size;
    count += selectedMachineIds.size;
    count += selectedLocations.size;
    return count;
  }, [statusFilter, selectedPriorities, selectedTypes, selectedMachineIds, selectedLocations]);

  // Flat location names for phone bottom sheet
  const flatLocationNames = useMemo(() => {
    const names: string[] = [];
    locations.forEach((parent) => {
      parent.children.forEach((child) => {
        if (!names.includes(child.name)) names.push(child.name);
      });
    });
    return names;
  }, [locations]);

  // Filtered machines for phone bottom sheet search
  const filteredTempMachines = useMemo(() => {
    if (!tempMachineSearch.trim()) return machines;
    const term = tempMachineSearch.toLowerCase();
    return machines.filter(m =>
      m.name.toLowerCase().includes(term) || m.sn.toLowerCase().includes(term)
    );
  }, [machines, tempMachineSearch]);

  // ─── Status change handler ─────────────────────────────────
  const handleStatusChange = useCallback(
    async (id: string, newStatusId: number, completionNotes?: string, createServiceReport?: boolean) => {
      await dispatch(changeWorkOrderStatus({
        id,
        model: {
          newStatusId,
          completionNotes,
          createServiceReport: createServiceReport || false,
        },
      })).unwrap();
      dispatch(fetchMyTasks());
    },
    [dispatch]
  );

  const handleNavigate = useCallback(
    (id: string) => navigate(`/operations/work-orders/${id}`),
    [navigate]
  );

  // ─── Completion dialog handlers ───────────────────────────
  const handleRequestCompletion = useCallback(
    (wo: WorkOrder) => setCompletionTarget(wo),
    []
  );

  const handleCompletionConfirm = useCallback(
    async (completionNotes: string, cost: number | undefined, createSR: boolean) => {
      if (!completionTarget) return;
      await dispatch(changeWorkOrderStatus({
        id: completionTarget.id,
        model: {
          newStatusId: WorkOrderStatusCode.PendingReview,
          completionNotes,
          cost,
          createServiceReport: createSR,
        },
      })).unwrap();
      setCompletionTarget(null);
      dispatch(fetchMyTasks());
    },
    [completionTarget, dispatch]
  );

  const handleCompletionCancel = useCallback(
    () => setCompletionTarget(null),
    []
  );

  // ─── Build active filter chips ─────────────────────────────
  const activeFilterChips = useMemo(() => {
    const chips: { type: string; value: string | number; label: string; color?: string }[] = [];

    if (statusFilter !== "all") {
      const cfg = STATUS_CHIPS.find(s => s.key === statusFilter);
      if (cfg) chips.push({ type: "status", value: statusFilter, label: translate(cfg.labelKey) });
    }

    selectedPriorities.forEach(code => {
      const cfg = PRIORITY_CHIPS.find(p => p.code === code);
      if (cfg) chips.push({ type: "priority", value: code, label: translate(cfg.labelKey), color: cfg.color });
    });

    selectedTypes.forEach(code => {
      const cfg = TYPE_CHIPS.find(t => t.code === code);
      if (cfg) chips.push({ type: "type", value: code, label: translate(cfg.labelKey) });
    });

    selectedMachineIds.forEach(id => {
      const m = machines.find(m => m.id === id);
      if (m) chips.push({ type: "machine", value: id, label: m.name });
    });

    selectedLocations.forEach(name => {
      chips.push({ type: "location", value: name, label: name });
    });

    if (searchTerm.trim()) {
      chips.push({ type: "search", value: "search", label: `"${searchTerm.trim()}"` });
    }

    return chips;
  }, [statusFilter, selectedPriorities, selectedTypes, selectedMachineIds, selectedLocations, searchTerm, translate, machines]);

  // ─── Status tab count ─────────────────────────────────────
  const statusCounts = useMemo(() => {
    let notStarted = 0, inProgress = 0, onHold = 0, resolved = 0;
    for (const t of myTasks) {
      if (t.workOrderStatusId === WorkOrderStatusCode.Assigned || t.workOrderStatusId === WorkOrderStatusCode.Scheduled) notStarted++;
      else if (t.workOrderStatusId === WorkOrderStatusCode.InProgress) inProgress++;
      else if (t.workOrderStatusId === WorkOrderStatusCode.OnHold) onHold++;
      else if (t.workOrderStatusId === WorkOrderStatusCode.Completed || t.workOrderStatusId === WorkOrderStatusCode.Verified || t.workOrderStatusId === WorkOrderStatusCode.PendingReview) resolved++;
    }
    return { all: myTasks.length, notStarted, inProgress, onHold, resolved };
  }, [myTasks]);

  const getStatusCount = (key: StatusFilter): number => {
    switch (key) {
      case "all": return statusCounts.all;
      case "notStarted": return statusCounts.notStarted;
      case "inProgress": return statusCounts.inProgress;
      case "onHold": return statusCounts.onHold;
      case "resolved": return statusCounts.resolved;
      default: return 0;
    }
  };

  // ─── Filtered machines for dropdown ────────────────────────
  const filteredMachines = useMemo(() => {
    if (!machineSearch.trim()) return machines;
    const term = machineSearch.toLowerCase();
    return machines.filter(m =>
      m.name.toLowerCase().includes(term) || m.sn.toLowerCase().includes(term)
    );
  }, [machines, machineSearch]);

  // ─── Status tab styling (rounded rectangle per Paper L6S-0) ──
  const statusTabSx = (active: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 18px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: active ? "#0C1117" : "#F2F0EB",
    color: active ? "#FFFFFF" : "#444F57",
    fontFamily: dt.font,
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
    boxSizing: "border-box" as const,
    "&:hover": {
      backgroundColor: active ? "#1a1a2e" : "#EDEAE5",
    },
  });

  // ─── Priority chip styling (colored dot + text, rounded outline) ──
  const priorityChipSx = (active: boolean, isCritical?: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 16px",
    borderRadius: "20px",
    border: active ? "1.5px solid #0C1117" : "1.5px solid #E8E5DF",
    backgroundColor: active ? "#0C1117" : (isCritical ? "#FEF2F2" : "#FFFFFF"),
    color: active ? "#FFFFFF" : "#444F57",
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
    height: "32px",
    boxSizing: "border-box" as const,
    "&:hover": {
      borderColor: active ? "#0C1117" : "#9CA3AF",
      backgroundColor: active ? "#1a1a2e" : (isCritical ? "#FDE8E8" : "#F7F5F0"),
    },
  });

  // ─── Dropdown trigger styling (muted text + chevron on cream bg) ──
  const dropdownTriggerSx = (active: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "6px 14px",
    borderRadius: "8px",
    border: "none" as const,
    backgroundColor: active ? "#0C1117" : "#F7F5F0",
    color: active ? "#FFFFFF" : "#8C9099",
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
    height: "32px",
    boxSizing: "border-box" as const,
    "&:hover": {
      backgroundColor: active ? "#1a1a2e" : "#EDEAE5",
      color: active ? "#FFFFFF" : "#444F57",
    },
  });

  // ─── Render ────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F2F0EB",
        backgroundImage: "radial-gradient(rgba(212,87,43,0.03) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        padding: isPhone ? "12px" : isTablet ? "20px" : "28px 40px",
      }}
    >
      {/* Centered content container */}
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, margin: "0 auto", width: "100%" }}>

        {/* ─── Filter Card Container (white card wrapping all filter rows) ─── */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "10px",
            marginBottom: isPhone ? "12px" : "16px",
            animation: "fadeIn 0.3s ease both",
            overflow: "hidden",
          }}
        >
          {/* ─── Row 1: Status tabs (+ phone filter button / desktop controls) ─── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              padding: isPhone ? "10px 12px" : isTablet ? "12px 18px" : "14px 20px",
            }}
          >
            {/* Status tab pills — on phone, full-width horizontal scroller */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              ...(isPhone ? {
                width: "100%",
                minWidth: 0,
                flexWrap: "nowrap",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              } : {
                flexWrap: "wrap",
                overflow: "visible",
                flexShrink: 0,
              }),
            }}>
              {STATUS_CHIPS.map(chip => {
                const count = getStatusCount(chip.key);
                const active = statusFilter === chip.key;
                return (
                  <Box
                    key={chip.key}
                    onClick={() => setStatusFilter(chip.key)}
                    sx={{
                      ...statusTabSx(active),
                      ...(isPhone ? { flexShrink: 0 } : {}),
                    }}
                  >
                    {translate(chip.labelKey)}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: dt.fontMono,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: active ? "rgba(255,255,255,0.7)" : "#6B7280",
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Phone: Date + Filter icon button — Row 2 (full-width, with space-between) */}
            {isPhone && (
              <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", mt: "2px" }}>
                <Box
                  onClick={(e) => setDateRangeMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444F57",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5" },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                  {translate(DATE_RANGES.find(o => o.key === dateRange)?.labelKey || "myTasks.dateRange.last30days")}
                </Box>

                {/* Filter button with badge */}
                <Box
                  onClick={openPhoneFilters}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "30px",
                    height: "30px",
                    border: "1px solid #E0DDD7",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: activeFilterCount > 0 ? "#D4572B" : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: activeFilterCount > 0 ? "#B84A24" : "#F5F4F1" },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: "16px", color: activeFilterCount > 0 ? "#FFFFFF" : "#4A4F57" }} />
                  {activeFilterCount > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-4px",
                        right: "-4px",
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        backgroundColor: "#D4572B",
                        border: "2px solid #FFFFFF",
                        color: "#FFFFFF",
                        fontSize: "9px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: dt.fontMono,
                      }}
                    >
                      {activeFilterCount}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Desktop: Sort + Date Range + Search on same row as tabs */}
            {isDesktop && (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
                {/* Sort dropdown */}
                <Box
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#8C9099",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5", color: "#444F57" },
                  }}
                >
                  <span style={{ color: "#9CA3AF", marginRight: "2px" }}>Sort:</span>
                  <span style={{ color: "#444F57", fontWeight: 600 }}>
                    {translate(SORT_OPTIONS.find(s => s.key === sortMode)?.labelKey || "myTasks.sort.newestFirst")}
                  </span>
                  <ArrowDownIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                </Box>

                {/* Date range */}
                <Box
                  onClick={(e) => setDateRangeMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444F57",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5" },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                  {translate(DATE_RANGES.find(o => o.key === dateRange)?.labelKey || "myTasks.dateRange.last30days")}
                </Box>

                {/* Search */}
                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  {searchExpanded ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "#F7F5F0",
                        border: "1px solid #D4572B",
                        borderRadius: "8px",
                        padding: "6px 14px",
                        height: "36px",
                        boxSizing: "border-box",
                        boxShadow: "0 0 0 3px rgba(212,87,43,0.08)",
                        animation: "fadeIn 0.2s ease",
                      }}
                    >
                      <SearchOutlined sx={{ fontSize: 16, color: "#D4572B" }} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={translate("myTasks.searchPlaceholder")}
                        autoFocus
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontFamily: "var(--font-sans)",
                          fontSize: "13px",
                          color: "#0C1117",
                          width: "180px",
                        }}
                      />
                      <CloseIcon
                        sx={{
                          fontSize: 14,
                          color: "#9CA3AF",
                          cursor: "pointer",
                          "&:hover": { color: "#D4572B" },
                        }}
                        onClick={() => { setSearchTerm(""); setSearchExpanded(false); }}
                      />
                    </Box>
                  ) : (
                    <Box
                      onClick={() => { setSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "#F7F5F0",
                        borderRadius: "8px",
                        padding: "8px 14px",
                        cursor: "pointer",
                        fontFamily: dt.font,
                        fontSize: "13px",
                        color: "#9CA3AF",
                        height: "36px",
                        boxSizing: "border-box",
                        transition: "all 0.15s ease",
                        "&:hover": { backgroundColor: "#EDEAE5", color: "#D4572B" },
                      }}
                    >
                      <SearchOutlined sx={{ fontSize: 16 }} />
                      Search...
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Phone: search bar below tabs */}
          {isPhone && (
            <Box sx={{ px: "12px", pb: "8px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#F7F5F0",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  height: "36px",
                  boxSizing: "border-box",
                }}
              >
                <SearchOutlined sx={{ fontSize: 16, color: "#9CA3AF" }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={translate("myTasks.searchPlaceholder")}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontFamily: "var(--font-sans)",
                    fontSize: "13px",
                    color: "#0C1117",
                    width: "100%",
                  }}
                />
                {searchTerm && (
                  <CloseIcon
                    sx={{
                      fontSize: 14,
                      color: "#9CA3AF",
                      cursor: "pointer",
                      "&:hover": { color: "#D4572B" },
                    }}
                    onClick={() => setSearchTerm("")}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Tablet: Sort/DateRange/Search controls row below tabs */}
          {isTablet && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "18px",
                pb: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                animation: "fadeSlideUp 0.25s ease both",
              }}
            >
              {/* Sort dropdown */}
              <Box
                onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: "#F7F5F0",
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#8C9099",
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  height: "36px",
                  boxSizing: "border-box",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#EDEAE5", color: "#444F57" },
                }}
              >
                <span style={{ color: "#9CA3AF", marginRight: "2px" }}>Sort:</span>
                <span style={{ color: "#444F57", fontWeight: 600 }}>
                  {translate(SORT_OPTIONS.find(s => s.key === sortMode)?.labelKey || "myTasks.sort.newestFirst")}
                </span>
                <ArrowDownIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
              </Box>

              {/* Date range */}
              <Box
                onClick={(e) => setDateRangeMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: "#F7F5F0",
                  fontFamily: dt.fontMono,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#444F57",
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  height: "36px",
                  boxSizing: "border-box",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#EDEAE5" },
                }}
              >
                <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                {translate(DATE_RANGES.find(o => o.key === dateRange)?.labelKey || "myTasks.dateRange.last30days")}
              </Box>

              {/* Search — icon-only toggle for tablet */}
              <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                {searchExpanded ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: "#F7F5F0",
                      border: "1px solid #D4572B",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      height: "36px",
                      boxSizing: "border-box",
                      boxShadow: "0 0 0 3px rgba(212,87,43,0.08)",
                      animation: "fadeIn 0.2s ease",
                    }}
                  >
                    <SearchOutlined sx={{ fontSize: 16, color: "#D4572B" }} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={translate("myTasks.searchPlaceholder")}
                      autoFocus
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontFamily: "var(--font-sans)",
                        fontSize: "13px",
                        color: "#0C1117",
                        width: "160px",
                      }}
                    />
                    <CloseIcon
                      sx={{
                        fontSize: 14,
                        color: "#9CA3AF",
                        cursor: "pointer",
                        "&:hover": { color: "#D4572B" },
                      }}
                      onClick={() => { setSearchTerm(""); setSearchExpanded(false); }}
                    />
                  </Box>
                ) : (
                  <Box
                    onClick={() => { setSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#F7F5F0",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "#EDEAE5", color: "#D4572B" },
                    }}
                  >
                    <SearchOutlined sx={{ fontSize: 16, color: "#9CA3AF" }} />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Sort + Date Range menus (shared between desktop/tablet) */}
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={() => setSortMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                border: "1px solid #E5E7EB",
                mt: "4px",
                "& .MuiMenuItem-root": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  padding: "8px 16px",
                  gap: "8px",
                },
              },
            }}
          >
            {SORT_OPTIONS.map(opt => (
              <MenuItem
                key={opt.key}
                selected={sortMode === opt.key}
                onClick={() => { setSortMode(opt.key); setSortMenuAnchor(null); }}
              >
                {sortMode === opt.key && <CheckIcon sx={{ fontSize: 14, color: "#D4572B" }} />}
                {translate(opt.labelKey)}
              </MenuItem>
            ))}
          </Menu>
          <Menu
            anchorEl={dateRangeMenuAnchor}
            open={Boolean(dateRangeMenuAnchor)}
            onClose={() => setDateRangeMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                border: "1px solid #E5E7EB",
                mt: "4px",
                "& .MuiMenuItem-root": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  padding: "8px 16px",
                  gap: "8px",
                },
              },
            }}
          >
            {DATE_RANGES.map(opt => (
              <MenuItem
                key={opt.key}
                selected={dateRange === opt.key}
                onClick={() => { setDateRange(opt.key); setDateRangeMenuAnchor(null); }}
              >
                {dateRange === opt.key && <CheckIcon sx={{ fontSize: 14, color: "#D4572B" }} />}
                {translate(opt.labelKey)}
              </MenuItem>
            ))}
          </Menu>

          {/* Divider between Row 1 and Row 2 (desktop/tablet only) */}
          {!isPhone && <Box sx={{ height: "1px", backgroundColor: "#E8E5DF" }} />}

          {/* ─── Filter Row 2: Priority chips + Dropdowns (desktop/tablet only) ─── */}
          {!isPhone && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isTablet ? "6px" : "8px",
              flexWrap: "wrap",
              padding: isTablet ? "10px 18px" : "12px 20px",
            }}
          >
          {/* Priority chips */}
          {PRIORITY_CHIPS.map(chip => {
            const active = selectedPriorities.has(chip.code);
            const isCritical = chip.code === WorkOrderPriorityCode.Critical;
            return (
              <Box
                key={chip.code}
                onClick={() => togglePriority(chip.code)}
                sx={priorityChipSx(active, isCritical)}
              >
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: active ? "#FFFFFF" : chip.color,
                  flexShrink: 0,
                  border: active ? "none" : `1.5px solid ${chip.color}`,
                  boxSizing: "border-box",
                }} />
                {translate(chip.labelKey)}
              </Box>
            );
          })}

          {/* Divider */}
          <Box sx={{ width: "1px", height: "20px", backgroundColor: "#E8E5DF", mx: "4px" }} />

          {/* Machine dropdown */}
          {machines.length > 0 && (
            <>
              <Box
                onClick={(e) => setMachineMenuAnchor(e.currentTarget)}
                sx={dropdownTriggerSx(selectedMachineIds.size > 0)}
              >
                {translate("myTasks.filterMachine")}
                {selectedMachineIds.size > 0 && (
                  <Box sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    fontWeight: 700,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    padding: "0 5px",
                    lineHeight: "16px",
                  }}>
                    {selectedMachineIds.size}
                  </Box>
                )}
                <ArrowDownIcon sx={{ fontSize: 14 }} />
              </Box>
              <Menu
                anchorEl={machineMenuAnchor}
                open={Boolean(machineMenuAnchor)}
                onClose={() => { setMachineMenuAnchor(null); setMachineSearch(""); }}
                PaperProps={{
                  sx: {
                    borderRadius: "10px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                    border: "1px solid #E5E7EB",
                    mt: "4px",
                    maxHeight: "300px",
                    minWidth: "240px",
                  },
                }}
              >
                {/* Search within dropdown */}
                <Box sx={{ px: "12px", py: "6px", borderBottom: "1px solid #F3F4F6" }}>
                  <input
                    type="text"
                    value={machineSearch}
                    onChange={e => setMachineSearch(e.target.value)}
                    placeholder={translate("myTasks.searchMachines")}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontFamily: "var(--font-sans)",
                      fontSize: "13px",
                      color: "#0C1117",
                      width: "100%",
                      padding: "4px 0",
                    }}
                  />
                </Box>
                {filteredMachines.map(m => (
                  <MenuItem
                    key={m.id}
                    onClick={() => toggleMachine(m.id)}
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "13px",
                      padding: "6px 12px",
                      gap: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Checkbox
                        checked={selectedMachineIds.has(m.id)}
                        size="small"
                        sx={{
                          padding: 0,
                          color: "#E8E5DF",
                          "&.Mui-checked": { color: "#D4572B" },
                        }}
                      />
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{m.name}</Box>
                        {m.sn && (
                          <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#9CA3AF" }}>
                            SN:{m.sn}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      color: selectedMachineIds.has(m.id) ? "#D4572B" : "#9CA3AF",
                      fontWeight: 600,
                    }}>
                      {m.count} {m.count === 1 ? translate("myTasks.task") : translate("myTasks.tasks")}
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Location dropdown */}
          {locations.size > 0 && (
            <>
              <Box
                onClick={(e) => setLocationMenuAnchor(e.currentTarget)}
                sx={dropdownTriggerSx(selectedLocations.size > 0)}
              >
                {translate("myTasks.filterLocation")}
                {selectedLocations.size > 0 && (
                  <Box sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    fontWeight: 700,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    padding: "0 5px",
                    lineHeight: "16px",
                  }}>
                    {selectedLocations.size}
                  </Box>
                )}
                <ArrowDownIcon sx={{ fontSize: 14 }} />
              </Box>
              <Menu
                anchorEl={locationMenuAnchor}
                open={Boolean(locationMenuAnchor)}
                onClose={() => setLocationMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    borderRadius: "10px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                    border: "1px solid #E5E7EB",
                    mt: "4px",
                    maxHeight: "300px",
                    minWidth: "220px",
                  },
                }}
              >
                {Array.from(locations.entries()).map(([parentKey, parent]) => (
                  <Box key={parentKey}>
                    {/* Parent header */}
                    <Box sx={{
                      padding: "8px 16px 2px",
                      fontFamily: dt.font,
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#9CA3AF",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}>
                      {parent.name}
                    </Box>
                    {Array.from(parent.children.entries()).map(([childKey, child]) => (
                      <MenuItem
                        key={childKey}
                        onClick={() => toggleLocation(child.name)}
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "13px",
                          padding: "4px 16px 4px 24px",
                          gap: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Checkbox
                            checked={selectedLocations.has(child.name)}
                            size="small"
                            sx={{
                              padding: 0,
                              color: "#E8E5DF",
                              "&.Mui-checked": { color: "#D4572B" },
                            }}
                          />
                          {child.name}
                        </Box>
                        <Box sx={{
                          fontFamily: dt.fontMono,
                          fontSize: "11px",
                          color: "#9CA3AF",
                        }}>
                          {child.count}
                        </Box>
                      </MenuItem>
                    ))}
                  </Box>
                ))}
              </Menu>
            </>
          )}

          {/* Type dropdown */}
          <Box
            onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
            sx={dropdownTriggerSx(selectedTypes.size > 0)}
          >
            {translate("myTasks.filterType")}
            {selectedTypes.size > 0 && (
              <Box sx={{
                fontFamily: dt.fontMono,
                fontSize: "10px",
                fontWeight: 700,
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "8px",
                padding: "0 5px",
                lineHeight: "16px",
              }}>
                {selectedTypes.size}
              </Box>
            )}
            <ArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu
            anchorEl={typeMenuAnchor}
            open={Boolean(typeMenuAnchor)}
            onClose={() => setTypeMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                border: "1px solid #E5E7EB",
                mt: "4px",
                minWidth: "180px",
                "& .MuiMenuItem-root": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  padding: "6px 12px",
                  gap: "8px",
                },
              },
            }}
          >
            {TYPE_CHIPS.map(chip => (
              <MenuItem
                key={chip.code}
                onClick={() => toggleType(chip.code)}
              >
                <Checkbox
                  checked={selectedTypes.has(chip.code)}
                  size="small"
                  sx={{
                    padding: 0,
                    color: "#E8E5DF",
                    "&.Mui-checked": { color: "#D4572B" },
                  }}
                />
                {translate(chip.labelKey)}
              </MenuItem>
            ))}
          </Menu>

          {/* Clear filters (right-aligned) */}
          {hasActiveFilters && (
            <Box
              onClick={clearAllFilters}
              sx={{
                marginLeft: "auto",
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#9CA3AF",
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { color: "#D4572B" },
              }}
            >
              {translate("myTasks.clearFilters")}
            </Box>
          )}
        </Box>
        )}

        {/* ─── Active Filter Chips Row (inside white card, desktop/tablet only) ─── */}
        {!isPhone && activeFilterChips.length > 0 && (
          <>
            {/* Divider between Row 2 and active chips row */}
            <Box sx={{ height: "1px", backgroundColor: "#E8E5DF" }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
                padding: isTablet ? "10px 18px" : "12px 20px",
                animation: "fadeIn 0.2s ease both",
              }}
            >
              {activeFilterChips.map((chip, i) => (
                <Box
                  key={`${chip.type}-${chip.value}-${i}`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px 4px 10px",
                    borderRadius: "16px",
                    backgroundColor: "#F2F0EB",
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#444F57",
                  }}
                >
                  {chip.color && (
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: chip.color, flexShrink: 0 }} />
                  )}
                  {chip.label}
                  <CloseIcon
                    sx={{
                      fontSize: 12,
                      color: "#9CA3AF",
                      cursor: "pointer",
                      "&:hover": { color: "#D4572B" },
                    }}
                    onClick={() => removeFilterChip(chip.type, chip.value)}
                  />
                </Box>
              ))}
              {/* Fix #12: Vertical divider before "Clear all" */}
              <Box sx={{ width: "1px", height: "20px", backgroundColor: "#E8E5DF", mx: "4px" }} />
              {/* Fix #14: font-weight 500 instead of 600 */}
              <Box
                onClick={clearAllFilters}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#D4572B",
                  cursor: "pointer",
                  padding: "4px 8px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {translate("myTasks.clearAll")}
              </Box>
            </Box>
          </>
        )}

        {/* Phone: active filter chips row (below white card, above task list) */}
        {isPhone && activeFilterChips.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "wrap",
              padding: "6px 0 8px 0",
              animation: "fadeIn 0.2s ease both",
            }}
          >
            {activeFilterChips.map((chip, i) => (
              <Box
                key={`${chip.type}-${chip.value}-${i}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px 4px 10px",
                  borderRadius: "16px",
                  backgroundColor: "#FFFFFF",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#444F57",
                }}
              >
                {chip.color && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: chip.color, flexShrink: 0 }} />
                )}
                {chip.label}
                <CloseIcon
                  sx={{
                    fontSize: 12,
                    color: "#9CA3AF",
                    cursor: "pointer",
                    "&:hover": { color: "#D4572B" },
                  }}
                  onClick={() => removeFilterChip(chip.type, chip.value)}
                />
              </Box>
            ))}
            <Box
              onClick={clearAllFilters}
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#D4572B",
                cursor: "pointer",
                padding: "4px 8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("myTasks.clearAll")}
            </Box>
          </Box>
        )}

        {/* Close the white filter card container */}
        </Box>

        {/* ─── Task list ────────────────────────────────────────── */}
        {myTasksLoading && myTasks.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <Loader />
          </Box>
        ) : filteredAndSorted.length === 0 ? (
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "14px",
              border: "1px solid #E5E7EB",
              padding: "48px 24px",
              textAlign: "center",
              animation: "fadeIn 0.4s ease both",
            }}
          >
            <TableEmpty />
            <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 600, color: "#0C1117", marginTop: "16px" }}>
              {hasActiveFilters ? translate("myTasks.noMatchingTasks") : translate("myTasks.noTasks")}
            </Box>
            <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: "#6B7280", marginTop: "6px" }}>
              {hasActiveFilters ? translate("myTasks.noMatchingTasksDescription") : translate("myTasks.noTasksDescription")}
            </Box>
            {hasActiveFilters && (
              <Box
                onClick={clearAllFilters}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#D4572B",
                  cursor: "pointer",
                  marginTop: "12px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {translate("myTasks.clearAll")}
              </Box>
            )}
          </Box>
        ) : timeHorizonGroups ? (
          /* ─── Grouped by time horizon (dueDate sort) ─────── */
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {timeHorizonGroups.map((group) => (
              <React.Fragment key={group.key}>
                {/* Section header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "16px 0 6px 0",
                    ...(group.key !== timeHorizonGroups[0].key && { marginTop: "8px" }),
                  }}
                >
                  {group.key === "overdue" && (
                    <WarningIcon sx={{ fontSize: 15, color: group.color }} />
                  )}
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      fontWeight: 700,
                      color: group.color,
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                    }}
                  >
                    {group.label} ({group.items.length})
                  </Box>
                </Box>

                {/* Cards in this group */}
                {group.items.map((task) => (
                  <TaskListItem
                    key={task.id}
                    workOrder={task}
                    onNavigate={handleNavigate}
                    onStatusChange={handleStatusChange}
                    onRequestCompletion={handleRequestCompletion}
                    canWrite={canWrite}
                    highlighted={changedIds.has(task.id)}
                  />
                ))}
              </React.Fragment>
            ))}
          </Box>
        ) : (
          /* ─── Flat list (non-dueDate sort) ────────────────── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {filteredAndSorted.map((task) => (
              <TaskListItem
                key={task.id}
                workOrder={task}
                onNavigate={handleNavigate}
                onStatusChange={handleStatusChange}
                onRequestCompletion={handleRequestCompletion}
                canWrite={canWrite}
                highlighted={changedIds.has(task.id)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ─── Phone: Filter Bottom Sheet ─── */}
      <Drawer
        anchor="bottom"
        open={phoneFiltersOpen}
        onClose={() => setPhoneFiltersOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            maxHeight: "85vh",
            background: "#FFFFFF !important",
            backgroundColor: "#FFFFFF !important",
            color: "#0C1117 !important",
          },
        }}
      >
        <Box sx={{ px: "20px", pt: "12px", pb: "24px", overflowY: "auto" }}>
          {/* Drag handle */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: "16px" }}>
            <Box sx={{ width: "36px", height: "4px", borderRadius: "2px", backgroundColor: "#E8E5DF" }} />
          </Box>

          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "18px", fontWeight: 700, color: "#0C1117" }}>
              {translate("myTasks.filtersTitle")}
            </Box>
            <Box
              onClick={resetPhoneFilters}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                "&:hover": { color: "#D4572B" },
              }}
            >
              {translate("myTasks.reset")}
            </Box>
          </Box>

          {/* STATUS section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.statusSection")}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {STATUS_CHIPS.map(chip => {
                const isSelected = tempStatusFilter === chip.key;
                return (
                  <Box
                    key={chip.key}
                    onClick={() => setTempStatusFilter(chip.key)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      border: "none",
                      backgroundColor: isSelected ? "#0C1117" : "#F2F0EB",
                      color: isSelected ? "#FFFFFF" : "#444F57",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      userSelect: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    {translate(chip.labelKey)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* PRIORITY section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.prioritySection")}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {PRIORITY_CHIPS.map(chip => {
                const isSelected = tempPriorities.has(chip.code);
                return (
                  <Box
                    key={chip.code}
                    onClick={() => toggleTempPriority(chip.code)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border: isSelected ? "1.5px solid #0C1117" : "1.5px solid #E8E5DF",
                      backgroundColor: isSelected ? "#0C1117" : "#FFFFFF",
                      color: isSelected ? "#FFFFFF" : "#444F57",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      userSelect: "none",
                      height: "32px",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "#FFFFFF" : chip.color,
                      flexShrink: 0,
                      border: isSelected ? "none" : `1.5px solid ${chip.color}`,
                      boxSizing: "border-box",
                    }} />
                    {translate(chip.labelKey)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* MACHINE section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.machineSection")}
            </Box>
            {/* Search input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "10px 14px",
                mb: "8px",
              }}
            >
              <SearchOutlined sx={{ fontSize: 16, color: "#9CA3AF" }} />
              <input
                type="text"
                value={tempMachineSearch}
                onChange={e => setTempMachineSearch(e.target.value)}
                placeholder={translate("myTasks.searchMachines")}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontFamily: "var(--font-sans)",
                  fontSize: "13px",
                  color: "#0C1117",
                  width: "100%",
                }}
              />
            </Box>
            {/* Machine list */}
            {filteredTempMachines.length > 0 && (
              <Box sx={{ maxHeight: "120px", overflow: "auto" }}>
                {filteredTempMachines.map(m => {
                  const isChecked = tempMachineIds.has(m.id);
                  return (
                    <Box
                      key={m.id}
                      onClick={() => {
                        setTempMachineIds(prev => {
                          const next = new Set(prev);
                          if (next.has(m.id)) next.delete(m.id);
                          else next.add(m.id);
                          return next;
                        });
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        py: "6px",
                        px: "4px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#F7F5F0" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        sx={{ p: "2px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>{m.name}</Box>
                        {m.sn && <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", color: "#8C9099" }}>SN:{m.sn}</Box>}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* LOCATION section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.locationSection")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: tempLocations.size > 0 ? "#0C1117" : "#8C9099",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "12px 14px",
              }}
            >
              {tempLocations.size > 0
                ? Array.from(tempLocations).join(", ")
                : translate("myTasks.selectLocation")}
            </Box>
            {flatLocationNames.length > 0 && (
              <Box sx={{ mt: "8px", maxHeight: "120px", overflow: "auto" }}>
                {flatLocationNames.map(name => {
                  const isChecked = tempLocations.has(name);
                  return (
                    <Box
                      key={name}
                      onClick={() => {
                        setTempLocations(prev => {
                          const next = new Set(prev);
                          if (next.has(name)) next.delete(name);
                          else next.add(name);
                          return next;
                        });
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        py: "6px",
                        px: "4px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#F7F5F0" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        sx={{ p: "2px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                      />
                      <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>{name}</Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* TYPE section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.typeSection")}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {TYPE_CHIPS.map(chip => {
                const isSelected = tempTypes.has(chip.code);
                return (
                  <Box
                    key={chip.code}
                    onClick={() => toggleTempType(chip.code)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border: isSelected ? "1.5px solid #0C1117" : "1.5px solid #E8E5DF",
                      backgroundColor: isSelected ? "#0C1117" : "#FFFFFF",
                      color: isSelected ? "#FFFFFF" : "#444F57",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      userSelect: "none",
                      height: "32px",
                      boxSizing: "border-box",
                    }}
                  >
                    {translate(chip.labelKey)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* SORT BY section */}
          <Box sx={{ mb: "24px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("myTasks.sortBySection")}
            </Box>
            <Box sx={{ mt: "4px" }}>
              {SORT_OPTIONS.map(opt => {
                const isActive = tempSortMode === opt.key;
                return (
                  <Box
                    key={opt.key}
                    onClick={() => setTempSortMode(opt.key)}
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? "#D4572B" : "#4A4F57",
                      py: "8px",
                      px: "4px",
                      cursor: "pointer",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      "&:hover": { backgroundColor: "#F7F5F0" },
                    }}
                  >
                    {translate(opt.labelKey)}
                    {isActive && <CheckIcon sx={{ fontSize: "16px", color: "#D4572B" }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Apply Filters button */}
          <Button
            fullWidth
            onClick={applyPhoneFilters}
            sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 700,
              textTransform: "none",
              color: "#FFFFFF",
              backgroundColor: "#D4572B",
              borderRadius: "24px",
              height: "48px",
              mb: "10px",
              "&:hover": { backgroundColor: "#B84A24" },
            }}
          >
            {translate("myTasks.applyFilters")}
          </Button>

          {/* Reset link */}
          <Box
            onClick={resetPhoneFilters}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: "#8C9099",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { color: "#D4572B" },
            }}
          >
            {translate("myTasks.reset")}
          </Box>
        </Box>
      </Drawer>

      {/* Completion dialog (modal) */}
      <CompletionDialog
        open={!!completionTarget}
        workOrderId={completionTarget?.id || ""}
        workOrderNumber={completionTarget?.number || ""}
        hasMachine={!!completionTarget?.machineId}
        hasLinkedSRs={!!(completionTarget?.linkedServiceReports && completionTarget.linkedServiceReports.length > 0)}
        machineName={completionTarget?.machineName}
        onConfirm={handleCompletionConfirm}
        onCancel={handleCompletionCancel}
      />
    </Box>
  );
};

export default MyTasksPage;
